import { BREAKPOINTS, COLORS, ContentIcons, DropdownButton, FilterChip, LoadingIndicator, SearchBar, Size, SystemIcons, TextButton as Button } from '@laerdal/life-react-components';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectMarket, selectTranslateWithAi } from '../store/slices/globalConfigSlice';
import { marketList } from '../model/constants/marketContants';
import SearchApi from '../services/api/SearchApi';
import ArticleApi from '../services/api/ArticleApi';
import { ArticleSearchItemDto, FacetItemDto } from '../model/dto/articleSearchWithFiltersResponseDto';
import { PageWidthFull } from './PageWidthFull';
import { useMediaMatch } from 'rooks';

const Container = styled.div`
  display: flex;
  width: 100%;

  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const SearchWrapper = styled.div`
  align-self: stretch;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  ${BREAKPOINTS.MEDIUM}{
    flex-direction: row;
  }
`;

const Title = styled.div`
  color: #000;

  text-align: center;
  font-family: Lato;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 28.8px */
  ${BREAKPOINTS.MEDIUM}{
    font-size: 38px;
    font-weight: 600;
    line-height: 44px; /* 115.789% */
  }
`;

const IconCircle = styled.div`
  display: flex;
  width: 42px;
  height: 42px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  aspect-ratio: 1/1;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--life-color-primary-100, ${COLORS.primary_100});
`;

const SubText = styled.div`
  color: var(--life-color-text-subtle);
  text-align: center;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  ${BREAKPOINTS.MEDIUM}{
    /* L - Desktop/Paragraph */
    font-size: 20px;
    line-height: 150%; /* 30px */
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-self: stretch;
  gap: 8px;
`;

const SortRow = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: flex-start;
`;

const GrayBand = styled.div`
  padding: 24px 0;
  background: var(--Neutral-020-tint, #FAFAFA);
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
`;

const ArticleList = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 12px;
`;

const ArticleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  border: 1px solid var(--life-color-border-default, #CCC);
  border-radius: 8px;
  background: #FFF;
  overflow: hidden;
`;

const ArticleHeader = styled.button`
  display: flex;
  padding: 14px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  ${BREAKPOINTS.MEDIUM}{
    padding: 20px 32px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ChevronWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaBadge = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  gap: 4px;
  background: ${COLORS.neutral_100};
  color: ${COLORS.neutral_600};
  text-align: center;
  font-feature-settings: 'liga' off;
  /* Components/Bold/XS */
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px; /* 114.286% */

  svg {
    color: ${COLORS.neutral_600};
  }
`;

const ArticleTitle = styled.div<{ $expanded: boolean }>`
  color: ${({ $expanded }) => ($expanded ? '#000' : COLORS.neutral_600)};
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 125% */
  /* clamp to two lines, then ellipsis */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-width: 100%;
  ${BREAKPOINTS.MEDIUM}{
    font-size: 20px;
    line-height: 24px; /* 120% */
  }
`;

const TopicBadge = styled(MetaBadge)`
  background: ${COLORS.accent1_100};
`;

const ArticleContent = styled.div<{ $faded: boolean }>`
  position: relative;
  max-height: 200px;
  overflow: hidden;
  align-self: stretch;
  padding: 14px 16px;
  background: ${COLORS.neutral_20};
  ${BREAKPOINTS.MEDIUM}{
    padding: 20px 32px;
  }

  &::after {
    content: '';
    display: ${({ $faded }) => ($faded ? 'block' : 'none')};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to bottom, transparent, #FFF);
    pointer-events: none;
  }
`;

const ReadMoreRow = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: center;
  padding: 14px 16px;
  background: #FFF;
  ${BREAKPOINTS.MEDIUM}{
    padding: 20px 32px;
  }
`;

const SpinnerRow = styled.div`
  display: flex;
  justify-content: center;
  align-self: stretch;
  padding: 20px 32px;
`;

const NoResultsBox = styled.div`
  display: flex;
  align-self: stretch;
  height: 350px;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid ${COLORS.neutral_200};
  background: ${COLORS.neutral_20};
`;

const NoResultsIconCircle = styled.div`
  display: flex;
  width: 50px;
  height: 50px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: #E5E5E5;
`;

const NoResultsText = styled.div`
  /* Components/Regular/L */
  font-family: Lato;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 120% */
  color: ${COLORS.neutral_600};
  text-align: center;
`;


const PAGE_SIZE = 8;
const SHOW_ALL = '__all__';
const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  productLineCode?: string;
  onHasArticlesChange?: (hasArticles: boolean) => void;
}

const CantFindWhatYouNeed = (props: Props) => {
  const { t } = useTranslation('translation');

  const navigate = useNavigate()
  const { categoryLink } = useParams<{ categoryLink: string }>();
  const currentMarket = useSelector(selectMarket);
  const translateWithAi = useSelector(selectTranslateWithAi);
  const aiLang = (categoryLink !== 'medical-devices' && translateWithAi) ? marketList[currentMarket]?.locale : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
  const chipSize = isMediumScreen ? Size.Medium : Size.Small;
  const [searchTerm, setSearchTerm] = useState<string>(() => searchParams.get('q') ?? '');
  const [selectedTopic, setSelectedTopic] = useState<string>(SHOW_ALL);
  const [sortBy, setSortBy] = useState<string>('popular');
  const [items, setItems] = useState<ArticleSearchItemDto[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [topicFacets, setTopicFacets] = useState<FacetItemDto[]>([]);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [fetchedContent, setFetchedContent] = useState<Record<string, string>>({});
  const [contentLoading, setContentLoading] = useState<string | null>(null);
  const [overflowing, setOverflowing] = useState<Record<string, boolean>>({});
  // null = initial browse not resolved yet
  const [hasArticles, setHasArticles] = useState<boolean | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // article number from the URL to re-expand once results are loaded
  const pendingExpandRef = useRef<string | null>(searchParams.get('article'));
  const restoredSearchRef = useRef<boolean>(!!searchParams.get('q'));
  // articles already counted as viewed in this session
  const viewedRef = useRef<Set<string>>(new Set());

  const fetchPage = (term: string, topic: string, pageNumber: number, append: boolean, sort?: string) => {
    if (!props.productLineCode) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    // fresh search/filter: clear the old list so only the spinner shows until results arrive
    if (!append) {
      setItems([]);
      setTotalCount(0);
      setExpandedArticle(null);
    }
    SearchApi.ArticleSearchWithFilters(
      term.trim() || null,
      pageNumber,
      PAGE_SIZE,
      [props.productLineCode],
      topic !== SHOW_ALL ? [topic] : undefined,
      aiLang,
      controller.signal,
      sort ?? sortBy,
      true // light: content is lazy-fetched on expand
    )
      .then((data) => {
        if (append) {
          setItems(prev => [...prev, ...(data.items ?? [])]);
        } else {
          setItems(data.items ?? []);
        }
        if (!append && term.trim() && (data.items ?? []).length === 0) {
          posthog?.capture('SUPP-ProductLinePage-searchNoResults', {
            url: window.location.href,
            searchTerms: term,
            productLineCode: props.productLineCode,
          });
        }
        setTotalCount(data.totalCount ?? 0);
        // report availability from the unfiltered browse load only
        if (!append && !term.trim() && topic === SHOW_ALL) {
          setHasArticles((data.totalCount ?? 0) > 0);
          props.onHasArticlesChange?.((data.totalCount ?? 0) > 0);
        }
        // chips only from browse responses: those facets are product-line scoped,
        // query-mode facets may include topics from other product lines
        if (!term.trim()) {
          setTopicFacets(data.articleFilterFacets ?? []);
        }
        setPage(pageNumber);
        setLoading(false);
        // re-expand the article stored in the URL once its results page is in
        if (!append && pendingExpandRef.current) {
          const target = pendingExpandRef.current;
          pendingExpandRef.current = null;
          const found = (data.items ?? []).find(a => a.articleNumber === target);
          if (found) expandArticle(found, false);
        }
      })
      .catch(() => { });
  };

  // initial load; a search restored from the URL is fetched by the debounce effect,
  // but facets and availability still come from a browse response
  useEffect(() => {
    if (!props.productLineCode) return;
    // translated and original content are cached separately per fetch
    setFetchedContent({});
    if (restoredSearchRef.current) {
      const controller = new AbortController();
      SearchApi.ArticleSearchWithFilters(null, 0, 1, [props.productLineCode], undefined, aiLang, controller.signal, sortBy, true)
        .then((data) => {
          setHasArticles((data.totalCount ?? 0) > 0);
          props.onHasArticlesChange?.((data.totalCount ?? 0) > 0);
          setTopicFacets(data.articleFilterFacets ?? []);
        })
        .catch(() => { });
      return () => { controller.abort(); abortRef.current?.abort(); };
    }
    fetchPage('', SHOW_ALL, 0, false);
    return () => abortRef.current?.abort();
  }, [props.productLineCode, aiLang]);

  // the SearchBar component does not expose autoComplete, so set it on the DOM node
  useEffect(() => {
    document.getElementById('product-support-search')?.setAttribute('autocomplete', 'off');
  }, []);

  // debounced search-as-you-type (also covers clearing the input)
  useEffect(() => {
    if (!props.productLineCode) return;

    const timer = setTimeout(() => {
      if (searchTerm.trim() && !restoredSearchRef.current) {
        posthog?.capture('SUPP-AllSearch', {
          url: window.location.href,
          searchTerms: searchTerm,
          productLineCode: props.productLineCode,
          page: 'ProductLinePage',
        });
      }
      restoredSearchRef.current = false;
      fetchPage(searchTerm, selectedTopic, 0, false);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);  // eslint-disable-line

  // keep search term + expanded article in the URL so back navigation restores them
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const before = params.toString();
    const article = expandedArticle ?? pendingExpandRef.current;
    if (searchTerm.trim()) params.set('q', searchTerm.trim()); else params.delete('q');
    if (article) params.set('article', article); else params.delete('article');
    if (params.toString() !== before) setSearchParams(params, { replace: true });
  }, [searchTerm, expandedArticle]);  // eslint-disable-line

  const selectTopic = (topic: string) => {
    setSelectedTopic(topic);
    posthog?.capture('SUPP-ProductLinePage-filterClick', {
      url: window.location.href,
      productLineCode: props.productLineCode,
      filterName: topic === SHOW_ALL ? 'Show all' : (topicFacets.find(f => f.value === topic)?.label ?? topic),
    });
    fetchPage(searchTerm, topic, 0, false);
  };

  const selectSort = (sort: string) => {
    setSortBy(sort);
    posthog?.capture('SUPP-ProductLinepage-SortbyClick', {
      url: window.location.href,
      productLineCode: props.productLineCode,
      tabName: sort === 'recent' ? 'Newest' : 'Most popular',
    });
    fetchPage(searchTerm, selectedTopic, 0, false, sort);
  };

  const toggleArticle = (article: ArticleSearchItemDto) => {
    if (expandedArticle === article.articleNumber) {
      setExpandedArticle(null);
      return;
    }
    expandArticle(article);
  };

  const expandArticle = (article: ArticleSearchItemDto, track: boolean = true) => {
    const num = article.articleNumber;
    setExpandedArticle(num);
    if (track) {
      posthog?.capture('SUPP-ProductLinePage-articleClick', {
        url: window.location.href,
        productLineCode: props.productLineCode,
        articleNumber: num,
      });
    }

    // inline search-result HTML is never translated, so with AI translation on we always fetch the article
    const content = aiLang ? fetchedContent[num] : (article.publicContent || article.content || fetchedContent[num]);
    if (!content) {
      setContentLoading(num);
      ArticleApi.GetKnowledgeArticle(num, undefined, undefined, aiLang, undefined, true)
        .then((a) => {
          setFetchedContent(prev => ({ ...prev, [num]: a?.publicContent || a?.content || '' }));
          setContentLoading(null);
        })
        .catch(() => setContentLoading(null));
    }
  };

  const measureContent = (num: string) => (el: HTMLDivElement | null) => {
    if (!el) return;
    const isOverflowing = el.scrollHeight > el.clientHeight;
    setOverflowing(prev => prev[num] === isOverflowing ? prev : { ...prev, [num]: isOverflowing });
    // fully visible without Read more counts as a view; lazily fetched content
    // is already counted server-side by the article GET
    if (!isOverflowing && el.innerHTML.trim() && !fetchedContent[num] && !viewedRef.current.has(num)) {
      viewedRef.current.add(num);
      ArticleApi.RegisterView(num);
    }
  };

  const executeSearch = () => {
    if (props.productLineCode) {
      // stay on the page: search inline immediately
      fetchPage(searchTerm, selectedTopic, 0, false);
      return;
    }
    posthog?.capture('SUPP-CantFindWhatYouNeed Search', {
      url: window.location.href,
      ref: document.referrer,
      searchTerm: searchTerm,
    });
    const params = new URLSearchParams();
    if (searchTerm) params.set('query', searchTerm);
    navigate(`/articles?${params.toString()}`);
  };

  const header = (
    <Container>
      <TitleRow>
        <IconCircle>
          <SystemIcons.Help size="38px" color={COLORS.primary_700} />
        </IconCircle>
        <Title>{t('Do you need help with this product?')}</Title>
      </TitleRow>
      <SubText>{t('Search for your issue or explore helpful resources, FAQs, and guidance to support you at every step.')}</SubText>
      <SearchWrapper>
        <SearchBar
          id="product-support-search"
          placeholder={t('Search using keywords')}
          searchTerm={searchTerm}
          enterSearch={(e) => {
            executeSearch();
          }}
          setSearchTerm={(term) => { setSearchTerm(term) }}

          removeSearch={() => {
            setSearchTerm('');
          }}
          size={Size.Small}
        />
      </SearchWrapper>
      {topicFacets.length > 0 &&
        <ChipsRow>
          <FilterChip size={chipSize} text={t('Show all')} selected={selectedTopic === SHOW_ALL} onClick={() => selectTopic(SHOW_ALL)} />
          {topicFacets.map((facet) =>
            <FilterChip size={chipSize} key={facet.value} text={facet.label} selected={selectedTopic === facet.value} onClick={() => selectTopic(facet.value)} />
          )}
        </ChipsRow>
      }
    </Container>
  );

  if (!props.productLineCode) {
    return header;
  }

  // product line has no articles at all: hide the whole FAQ section
  if (hasArticles === false) {
    return null;
  }

  return (
    <>
      <GrayBand>
        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto">
          {header}
        </PageWidthFull>
      </GrayBand>
      <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto">
        <ResultsContainer>
          {!searchTerm.trim() && items.length > 0 &&
            <SortRow>
              <DropdownButton
                type='text'
                itemsType='normal'
                size={Size.Small}
                alignLeft={true}
                items={[
                  { value: 'popular', displayLabel: t('Most popular') },
                  { value: 'recent', displayLabel: t('Most recent') },
                ]}
                value={[sortBy]}
                onClick={(values) => {
                  if (values.length > 0 && values[0] !== sortBy) selectSort(values[0]);
                }}
              />
            </SortRow>
          }
          {loading && items.length === 0 && <LoadingIndicator />}
          {!loading && items.length === 0 &&
            <NoResultsBox>
              <NoResultsIconCircle>
                <ContentIcons.PatientAssignment size="42px" color={COLORS.neutral_600} />
              </NoResultsIconCircle>
              <NoResultsText>
                {t('It looks like there are no results for your search, try')}<br />
                {t('searching again or get help from our support team.')}
              </NoResultsText>
            </NoResultsBox>
          }
          {items.length > 0 &&
            <ArticleList>
              {items.map((article) => {
                const num = article.articleNumber;
                const expanded = expandedArticle === num;
                const content = aiLang ? (fetchedContent[num] || '') : (article.publicContent || article.content || fetchedContent[num] || '');
                return (
                  <ArticleBox key={num}>
                    <ArticleHeader onClick={() => toggleArticle(article)}>
                      <HeaderLeft>
                        <MetaRow>
                          {(article.articleFilterNames ?? []).map((topic) =>
                            <TopicBadge key={topic}>{topic}</TopicBadge>
                          )}
                          <MetaBadge><SystemIcons.VisibleOn size="16px" />{article.viewCount} {t('views')}</MetaBadge>
                          <MetaBadge><SystemIcons.Time size="16px" />~ {article.estimatedReadTimeMinutes ?? 0} {t('mins')}</MetaBadge>
                        </MetaRow>
                        <ArticleTitle $expanded={expanded}>{article.title}</ArticleTitle>
                      </HeaderLeft>
                      <ChevronWrap>
                        {expanded ? <SystemIcons.ChevronUp size="24px" /> : <SystemIcons.ChevronDown size="24px" />}
                      </ChevronWrap>
                    </ArticleHeader>
                    {expanded && (contentLoading === num
                      ? <SpinnerRow><LoadingIndicator /></SpinnerRow>
                      : <>
                        <ArticleContent $faded={!!overflowing[num]} ref={measureContent(num)} dangerouslySetInnerHTML={{ __html: content }} />
                        {overflowing[num] &&
                          <ReadMoreRow>
                            <Button
                              variant='tertiary'
                              icon={<SystemIcons.ArrowLineRight />}
                              iconPosition='right'
                              onClick={() => {
                                posthog?.capture('SUPP-ProductLinePage-readmoreClick', {
                                  url: window.location.href,
                                  productLineCode: props.productLineCode,
                                  articleNumber: num,
                                });
                                navigate(`/articles/${num}`);
                              }}
                            >
                              {t('Read more')}
                            </Button>
                          </ReadMoreRow>
                        }
                      </>
                    )}
                  </ArticleBox>
                );
              })}
            </ArticleList>
          }
          {loading && items.length > 0 && <LoadingIndicator />}
          {!loading && items.length < totalCount &&
            <Button
              variant='tertiary'
              size={chipSize}
              icon={<SystemIcons.ChevronDown />}
              onClick={() => {
                posthog?.capture('SUPP-ProductLinepage-ShowmoreClick', {
                  url: window.location.href,
                  productLineCode: props.productLineCode,
                });
                fetchPage(searchTerm, selectedTopic, page + 1, true);
              }}
            >
              {t('Show more')}
            </Button>
          }
        </ResultsContainer>
      </PageWidthFull>
    </>
  );
}

export default CantFindWhatYouNeed;